import type { ScoreExplanation } from '../../api/types'
import { BandBadge, StatusBadge } from '../../components/ui'
import { formatScore } from '../../lib/bands'
import { levelLabel } from '../../lib/format'
import { coverageText, explainRounding, sourceDifference, thresholdRows } from './breakdown'
import { inspectionText, referenceBandLabel } from './presentation'

type Score = Pick<ScoreExplanation, 'band' | 'total' | 'raw_sum' | 'rounding' | 'scored_count' | 'criteria_count' | 'coverage' | 'breakdown' | 'thresholds' | 'warnings'> & Partial<Pick<ScoreExplanation, 'source_reported_total' | 'differs_from_source' | 'motor_reference' | 'operating_state'>>

/** The explainable score: per-criterion contribution and rule text, the sum, the rounding, the band and the thresholds. */
export function BreakdownView({ score }: { score: Score }) {
  const diff = sourceDifference({ source_reported_total: score.source_reported_total ?? null, differs_from_source: score.differs_from_source ?? null, total: score.total })
  const motor = score.motor_reference
  return (
    <div className="stack">
      <div className="score-hero">
        <BandBadge band={score.band} size="lg" />
        <span className="score-hero__total mono">{formatScore(score.total)}</span>
        <span className="muted">{coverageText(score.scored_count, score.criteria_count, score.coverage)}</span>
      </div>
      {diff ? <p role="note" className="callout callout--warn"><strong>{diff.title}.</strong> {diff.detail}</p> : null}
      {score.warnings.map((w) => <p key={w} className="callout">{inspectionText(w)}</p>)}
      <div className="table-wrap">
        <table className="data-table breakdown">
          <caption className="sr-only">Desglose del puntaje por criterio</caption>
          <thead><tr><th>#</th><th>Criterio</th><th>Nivel</th><th>Regla aplicada</th><th className="align-right">Aporte</th></tr></thead>
          <tbody>
            {score.breakdown.map((b) => (
              <tr key={b.criterion} className={b.status !== 'scored' ? 'is-muted' : undefined}>
                <td data-label="#">{b.number}</td>
                <td data-label="Criterio">{inspectionText(b.name)}</td>
                <td data-label="Nivel">{b.status === 'scored' ? levelLabel(b.level) : <StatusBadge tone="neutral" label={levelLabel(b.level)} />}</td>
                <td data-label="Regla aplicada">{inspectionText(b.rule_text)}</td>
                <td data-label="Aporte" className="align-right mono">{b.contribution ?? '—'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><th colSpan={4} scope="row">Suma de criterios puntuados</th><td className="align-right mono">{score.raw_sum ?? '—'}</td></tr>
          </tfoot>
        </table>
      </div>
      <p>{explainRounding(score.raw_sum, score.total, score.rounding)}</p>
      <div>
        <h3>Bandas utilizadas</h3>
        <ul className="threshold-list">
          {thresholdRows(score.thresholds, score.band).map((t) => (
            <li key={t.code} className={t.applied ? 'is-applied' : undefined}><BandBadge band={t.code} size="sm" /> <span className="mono">{t.range}</span>{t.applied ? <strong> aplica</strong> : null}</li>
          ))}
        </ul>
      </div>
      {motor ? (
        <div className="callout">
          <strong><span className="tag-reference">Referencia</span> diagnóstico del motor: {motor.total ?? '—'} ({referenceBandLabel(motor.band)})</strong>
          <p className="muted">Tomado de las fichas de los equipos y no validado por mantenimiento. Nunca cambia la banda oficial.</p>
        </div>
      ) : null}
    </div>
  )
}
